import { useMemo, useState } from 'react';
import { Button } from 'antd';

import DateField from '../common/DateField';
import FieldHelp, { elementHelpText } from '../common/FieldHelp';
import { CLOSE, PLUS } from '../icons';
import { minuteStepOf } from '../../utils/dateBounds';
import { normalizeDateList, precisionOf, weekdaysOf } from '../../utils/dateOptions';
import { validateElementValue } from '../../utils/elementValidation';

/**
 * A date element that holds SEVERAL dates (`options.allowMultiple`, see
 * utils/dateOptions): one date field per entry, stacked, with a remove beside
 * each and "Add another date" beneath them.
 *
 * A LIST OF FIELDS, NOT A CHIPS BOX. Each entry is the platform's own date
 * field, whole and unmodified — the grammar, the calendar, the step buttons,
 * the relative suffix, the native wheel on a phone, the greyed days a bound
 * or a weekday rule ask for. A chips input would have had to re-implement
 * every one of those, or drop them; three doses of a medicine deserve the
 * same field one dose gets.
 *
 * WHAT IS STORED is the canonical list — blanks dropped, ascending,
 * de-duplicated (`normalizeDateList`) — recomputed on every commit, which is
 * why the rows can reorder themselves under the reader after an entry is
 * filled in. That is the contract the API validates against and the shape
 * every reader here sorts and groups by, so the form shows it rather than
 * hiding a different order behind it.
 *
 * The BLANK rows are the one thing held in local state: a list has no room
 * for "an entry that has not been typed yet", so an empty row is a place to
 * type and nothing more until something is committed into it.
 *
 * Each entry is validated on its own — the bounds and the weekday rule apply
 * to every date, not to the span they cover — and its message sits under its
 * own row, because a field with six values and one sentence cannot say which
 * value the sentence is about.
 */
export default function MultiDateField( {
	element,
	value,
	onChange,
	labelBadge = null,
	disabled = false,
	required = false,
	min = null,
	max = null,
	incrementButtons = false,
	showRelativeTime = false,
	// The form's own message (the last Save, or an API refusal). It names the
	// element, not an entry, so it sits under the whole list.
	errorMessage = null,
	// What a bound that names another element is resolved against:
	// { elements, values } — utils/elementValidation.
	context = null,
} ) {

	const name = element?.name;
	const label = element?.label?.singular || name || '';
	const mode = element?.dataType;

	const committed = useMemo( () => normalizeDateList( mode, value ), [ mode, value ] );
	const [ blanks, setBlanks ] = useState( 0 );

	// At least one row, always: a field nobody can see is a field nobody can
	// fill in.
	const rowCount = Math.max( committed.length + blanks, 1 );
	const rows = Array.from( { length: rowCount }, ( _, index ) => committed[ index ] ?? null );

	const commit = ( next ) => {
		const list = normalizeDateList( mode, next );
		onChange( name, list.length ? list : [] );
	};

	const setEntry = ( index, next ) => {
		const draft = rows.slice();
		draft[ index ] = next;
		// A blank row that has just been filled in becomes a committed entry,
		// so the blank it was is no longer owed one.
		if ( next && index >= committed.length ) setBlanks( count => Math.max( 0, count - 1 ) );
		commit( draft );
	};

	const removeEntry = ( index ) => {
		if ( index >= committed.length ) {
			setBlanks( count => Math.max( 0, count - 1 ) );
			return;
		}
		commit( committed.filter( ( _, i ) => i !== index ) );
	};

	const weekdays = weekdaysOf( element );
	const precision = precisionOf( element );
	const minuteStep = minuteStepOf( element?.options );

	// Every entry against the element's own rules, live — the same reasoning
	// as a single date field (a date commits whole values, so there is no
	// half-typed state to wait for) and the same context, so an entry bounded
	// against another element re-checks the moment that element moves.
	const entryErrors = rows.map( entry => (
		entry ? validateElementValue( element, entry, context || undefined ) : null
	) );

	const hasError = !! errorMessage || entryErrors.some( Boolean );

	return (
		<div className={'multi-date-field' + ( hasError ? ' multi-date-field--error' : '' )}>
			<div className='field-label-row'>
				<FieldHelp text={elementHelpText( element )} label={label}>
					<label className='d-block' htmlFor={name}>
						<span className='multi-date-field__label-text'>
							{labelBadge ? <span className='multi-date-field__label-badge'>{labelBadge}</span> : null}
							<span>{label}</span>
						</span>
						{required ? <span className='required-indicator' aria-hidden='true'>*</span> : null}
					</label>
				</FieldHelp>
			</div>

			<div className='multi-date-field__rows'>
				{rows.map( ( entry, index ) => (
					<div className='multi-date-field__row' key={`${ name }-${ index }`}>
						<div className='multi-date-field__line'>
							<span className='multi-date-field__field'>
								<DateField
									mode={mode}
									// The FIRST entry keeps the element's own name, so the
									// label's `htmlFor` reaches a real control and the form's
									// "jump to the field" lands on something focusable.
									elementName={index === 0 ? name : `${ name }__${ index }`}
									elementLabel={`${ label } ${ index + 1 }`}
									value={entry}
									onChange={( _, next ) => setEntry( index, next )}
									disabled={disabled}
									required={required && index === 0}
									incrementButtons={incrementButtons}
									showRelativeTime={showRelativeTime}
									min={min}
									max={max}
									minuteStep={minuteStep}
									weekdays={weekdays}
									precision={precision}
									status={entryErrors[ index ] ? 'error' : undefined}
								/>
							</span>
							<Button
								type='text'
								size='large'
								className='multi-date-field__remove'
								icon={<CLOSE />}
								disabled={disabled || ( rows.length === 1 && ! entry )}
								aria-label={`Remove ${ label } ${ index + 1 }`}
								onClick={() => removeEntry( index )}
							/>
						</div>
						{entryErrors[ index ] ? (
							<div className='element-field__error' role='alert'>{entryErrors[ index ]}</div>
						) : null}
					</div>
				) )}
			</div>

			<Button
				type='dashed'
				className='multi-date-field__add'
				icon={<PLUS />}
				disabled={disabled}
				onClick={() => setBlanks( count => count + 1 )}
			>
				Add another date
			</Button>

			{errorMessage ? <div className='element-field__error' role='alert'>{errorMessage}</div> : null}
		</div>
	);
}
